import React from 'react';
import { format } from 'date-fns';

/**
 * Componente para exibir um evento no calendário (versão desktop)
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.event - Dados do evento
 * @param {Function} props.onSelect - Função chamada quando o evento é selecionado
 * @returns {JSX.Element} - Elemento React
 */
const TimelineEvent = ({ event, onSelect }) => {
  // Verificação de segurança: o evento precisa existir
  if (!event) return null;
  
  // Garantir que appointment existe
  const appointment = event.appointment || {};
  
  // Formatar horário de início e fim
  let timeString = '';
  try {
    timeString = `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`;
  } catch (error) {
    console.error('Erro ao formatar horários:', error);
  }
  
  // Função para lidar com cliques no evento
  const handleEventClick = (e) => {
    e.stopPropagation(); // Impedir propagação de eventos
    
    // Se onSelect foi fornecido, chamar com o evento de DOM e os dados do evento
    if (typeof onSelect === 'function') {
      onSelect({
        ...event,
        currentTarget: e.currentTarget // Passar o elemento atual para capturar posição
      });
    }
  };
  
  // Função para lidar com teclas (acessibilidade)
  const handleKeyPress = (e) => {
    // Abrir ao pressionar Enter ou Espaço
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      
      if (typeof onSelect === 'function') {
        onSelect({
          ...event,
          currentTarget: e.currentTarget // Passar o elemento atual para capturar posição
        });
      }
    }
  };
  
  // Determinar o ícone com base na origem do agendamento
  let sourceIcon;
  
  if (appointment.source === 'atendente' || appointment.deviceType === 'atendente') {
    // Ícone para agendamentos feitos pelo atendente
    sourceIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  } else if (appointment.source === 'mobile' || appointment.deviceType === 'mobile') {
    // Ícone para agendamentos feitos via dispositivo móvel
    sourceIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  } else {
    // Ícone para agendamentos feitos via desktop (padrão)
    sourceIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    );
  }
  
  // Determinar o ícone de status
  let statusIcon = null;
  if (appointment.status === 'canceled' || !!appointment.cancelado) {
    statusIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  } else if (!!appointment.profissionalConfirmado) {
    statusIcon = (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  // Descrição do serviço (se existir) com verificações de segurança
  let serviceName = null;
  if (appointment.servico) {
    serviceName = appointment.servico;
  } else if (appointment.servicos && Array.isArray(appointment.servicos) && appointment.servicos.length > 0) {
    serviceName = appointment.servicos
      .map(s => {
        if (!s) return 'Serviço';
        return s.nome || s.titulo || 'Serviço';
      })
      .join(', ');
  }

  // Versão desktop
  return (
    <div 
      className="rbc-event-content-wrapper text-xs p-0.5 w-full h-full cursor-pointer pointer-events-auto"
      role="button"
      tabIndex={0}
      onClick={handleEventClick}
      onKeyPress={handleKeyPress}
      aria-label={`Agendamento: ${event.title || ''} - ${timeString}`}
    >
      {/* Horário e origem */}
      <div className="flex justify-between items-center font-semibold mb-0.5 pointer-events-none">
        <span className="pointer-events-none">{timeString}</span>
        <div className="flex items-center pointer-events-none">
          {statusIcon}
          {sourceIcon}
        </div>
      </div>
      
      {/* Nome do cliente */}
      {event.title && (
        <div className="font-medium truncate pointer-events-none">{event.title}</div>
      )}
      
      {/* Serviço (se disponível) */}
      {serviceName && (
        <div className="text-xs opacity-90 truncate max-w-full pointer-events-none">
          {serviceName}
        </div>
      )}
      
      {/* Ícones e status adicionais */}
      {!!appointment.noShow && (
        <div className="text-xs mt-0.5 font-medium pointer-events-none">
          No-show
        </div>
      )}
    </div>
  );
};

export default TimelineEvent; 